import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Route, useLocation } from 'react-router-dom';
import useOgMetaRoutes from './use-og-meta-routes';
import {
	selectDefaultLocale,
	selectCurrentUrlLang,
	selectHtmlLang,
	selectIsBotRequest,
	selectLanguageProductSEOMap,
	selectLocales,
	selectSiteCode
} from '../brand/ducks/selectors';
import useTranslations from '../translation/use-translations';
import NullLoader from '~/components/null-loader.js';
import { SITE_CODES } from '~/components/brand/constants';
import { TracedRoutes } from '@comeon/error-reporter';
import {
	ROUTE_LIVEBETTING,
	ROUTE_SPLASH_TECH,
	ROUTE_SPORTSBOOK,
	ROUTE_SPORTSBOOK_SPORTS_PAGE,
	ROUTE_THUNDERBITE,
	ROUTE_TVBET,
	ROUTE_VIRTUALS,
	ROUTE_WE_SPIN
} from '~/components/router/constants';
import useCasinoRoutes from '~/components/router/use-casino-routes';

const findByLocale = (languages, locale) => languages.filter(lang => lang.locale === locale)[0];

const HelmetTagWrapper = () => {
	const locales = useSelector(selectLocales);
	const defaultLocale = useSelector(selectDefaultLocale);
	const currentUrlLang = useSelector(selectCurrentUrlLang);
	const htmlLang = useSelector(selectHtmlLang);
	const isComeon = useSelector(selectSiteCode) === SITE_CODES.COMEON;
	const isMobilebet = useSelector(selectSiteCode) === SITE_CODES.MOBILEBET;
	const isFolke = useSelector(selectSiteCode) === SITE_CODES.FOLKEAUTOMATEN;
	const isMobilautomaten = useSelector(selectSiteCode) === SITE_CODES.MOBILAUTOMATEN;
	const isGetlucky = useSelector(selectSiteCode) === SITE_CODES.GETLUCKY;
	const isNorgesspill = useSelector(selectSiteCode) === SITE_CODES.NORGESSPILL;
	const isCherry = useSelector(selectSiteCode) === SITE_CODES.CHERRYCASINO;

	const { ROUTE_CASINO, ROUTE_LIVE_CASINO } = useCasinoRoutes();
	const { ogRoutes, ogTextKeys } = useOgMetaRoutes();

	const productRoutes = [
		ROUTE_SPORTSBOOK,
		ROUTE_LIVEBETTING,
		ROUTE_CASINO,
		ROUTE_LIVE_CASINO,
		ROUTE_THUNDERBITE,
		ROUTE_SPLASH_TECH,
		ROUTE_WE_SPIN,
		ROUTE_TVBET,
		ROUTE_VIRTUALS
	];
	const skipSportsbookRoutes = [ROUTE_SPORTSBOOK_SPORTS_PAGE];
	const languageProductSEOMap = useSelector(selectLanguageProductSEOMap);
	const isBot = useSelector(selectIsBotRequest);

	const code = currentUrlLang === 'ca' ? 'on' : currentUrlLang;

	const path = useLocation()?.pathname ?? '';
	const langs = locales && locales.filter(locale => locale.langName !== 'gu');

	const { translations, isLoadingTranslations } = useTranslations(ogTextKeys.concat('SEO_NO_INDEX_TAG'));
	if (isLoadingTranslations) return null;

	const { SEO_NO_INDEX_TAG: noIndex } = translations;
	const noIndexRoutes = noIndex ? noIndex.split('\n') : noIndex;
	const { origin, protocol } = window.location;

	let xdefault = findByLocale(langs, 'en_XX');
	// fixme, find better way to add the english locale (or another workaround)
	// when filtering the list of locales on BE (ref PYRO-237)
	if (
		!xdefault &&
		(isComeon || isFolke || isMobilebet || isMobilautomaten || isGetlucky || isNorgesspill || isCherry)
	) {
		const englishLocale = { locale: 'en_XX', hrefLang: 'en', urlLang: 'en' };
		xdefault = Object.create(englishLocale);
	}
	xdefault = xdefault || findByLocale(langs, defaultLocale);

	if (skipSportsbookRoutes.some(route => path.includes(route))) {
		return null;
	}

	return (
		<>
			<Helmet>
				<html lang={htmlLang} />
				{isBot &&
					langs.map(locale => {
						// A bit of a hacky solution here but this is code should live in the BE side after refactoring, it also affects only bot-requests
						// We are checking that if that we are on a product page that product is available on other langs/franchises, if not available we just skip to add that hreflang
						const product = path.split('/')[1];

						if (productRoutes.includes(`/${product}`)) {
							// Skip /ca on product pages as requested
							if (locale.urlLang === 'ca') {
								return false;
							}
							if (!languageProductSEOMap[locale.urlLang]?.includes(product)) {
								return false;
							}
						}

						const href = `${
							locale.hrefDomain && locale.hrefDomain.length > 0
								? `${protocol}//${locale.hrefDomain}`
								: origin
						}/${locale.urlLang}${path === '/' ? '' : path}`;
						return <link key={locale.langName} rel="alternate" hrefLang={locale.hrefLang} href={href} />;
					})}
				{isBot && (
					<link
						rel="alternate"
						hrefLang="x-default"
						href={`${
							xdefault?.hrefDomain && xdefault?.hrefDomain.length > 0
								? `${protocol}//${xdefault?.hrefDomain}`
								: origin
						}/${xdefault?.urlLang}${path === '/' ? '' : path}`}
					/>
				)}
				{isBot && <link rel="canonical" href={`${origin}/${code}${path === '/' ? '' : path}`} />}
			</Helmet>
			{noIndexRoutes && (
				<TracedRoutes>
					{noIndexRoutes.map(routeEl => {
						const [aPath, content] = routeEl.split('=');
						return (
							<Route
								key={aPath}
								path={aPath}
								element={
									<Helmet>
										<meta name="robots" content={content} />
									</Helmet>
								}
							/>
						);
					})}
					<Route path="*" element={<NullLoader />} />
				</TracedRoutes>
			)}
			{ogTextKeys.length > 0 && (
				<TracedRoutes>
					{ogTextKeys
						.filter(textKey => translations[textKey])
						.map(textKey => {
							const aPath = ogRoutes[textKey];
							const metas = translations[textKey].split('\n');
							return (
								<Route
									key={`og:${aPath}`}
									path={aPath}
									element={
										<Helmet>
											{metas.map(meta => {
												const separatorIndex = meta.indexOf('=');
												const property = meta.substring(0, separatorIndex);
												const content = meta.substring(separatorIndex + 1);
												return (
													<meta
														key={`og:${property}`}
														property={`og:${property}`}
														content={content}
													/>
												);
											})}
										</Helmet>
									}
								/>
							);
						})}
					<Route path="*" element={<NullLoader />} />
				</TracedRoutes>
			)}
		</>
	);
};

export default HelmetTagWrapper;
